// src/components/PopupManager.js
//
// Storefront popup & announcement banner manager:
// - Fetches active popups from Firestore
// - Listens to route transitions (useLocation)
// - Filters by active status, date validity, target pages, and frequency capping
// - Renders top banners and priority-queued modals/cards
// - Supports multiple concurrent popups without awkward backdrop collisions
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRight, X } from 'lucide-react';
import { getActivePopups } from '../db';
import { getThemePalette } from '../popupTheme';

const DISMISSED_PREFIX = 'ndizachem_popup_dismissed_';

/**
 * Checks if a popup path matches the current pathname.
 */
export const matchesPath = (targetPages, currentPath) => {
  if (!targetPages || targetPages.length === 0) return true;
  if (targetPages.includes('*') || targetPages.includes('all')) return true;

  // Normalize path (remove trailing slash unless root)
  const normCurrent = currentPath === '/' ? '/' : currentPath.replace(/\/+$/, '');

  return targetPages.some((pattern) => {
    if (!pattern) return false;
    const normPattern = pattern === '/' ? '/' : pattern.replace(/\/+$/, '');
    if (normPattern === normCurrent) return true;
    // Prefix wildcard, e.g. /products/*
    if (normPattern.endsWith('/*')) {
      const prefix = normPattern.slice(0, -2);
      return normCurrent.startsWith(prefix);
    }
    return false;
  });
};

/**
 * Checks if a popup has been dismissed according to its frequency capping rule.
 */
export const isDismissed = (popupId, dismissFrequency, pageDismissedSet) => {
  if (pageDismissedSet.has(popupId)) return true;

  try {
    if (dismissFrequency === 'once_ever') {
      return localStorage.getItem(`${DISMISSED_PREFIX}${popupId}`) === 'true';
    }
    if (dismissFrequency === 'once_per_session') {
      return sessionStorage.getItem(`${DISMISSED_PREFIX}${popupId}`) === 'true';
    }
  } catch (err) {
    // Local storage access error (e.g. private browsing restriction)
    return false;
  }
  return false;
};

/**
 * Marks a popup as dismissed.
 */
export const recordDismissal = (popupId, dismissFrequency) => {
  try {
    if (dismissFrequency === 'once_ever') {
      localStorage.setItem(`${DISMISSED_PREFIX}${popupId}`, 'true');
    } else if (dismissFrequency === 'once_per_session') {
      sessionStorage.setItem(`${DISMISSED_PREFIX}${popupId}`, 'true');
    }
  } catch (err) {
    // Ignore storage errors
  }
};

/**
 * Top Announcement Banner Component (Docked at top of screen)
 */
const BannerItem = ({ popup, onDismiss, onCtaClick }) => {
  const pal = getThemePalette(popup.theme);
  const isDark = popup.theme === 'dark';

  return (
    <Box
      sx={{
        backgroundColor: pal.bg,
        borderBottom: `1px solid ${pal.border}`,
        color: isDark ? pal.textPrimary : '#0f172a',
        px: { xs: 2, sm: 3 },
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        position: 'relative',
        zIndex: 1100,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          flexGrow: 1,
          justifyContent: { xs: 'flex-start', md: 'center' },
        }}
      >
        {popup.badge && (
          <Chip
            label={popup.badge}
            size="small"
            sx={{
              backgroundColor: pal.badgeBg,
              color: pal.badgeText,
              fontWeight: 800,
              fontSize: '0.68rem',
              height: 22,
            }}
          />
        )}
        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? pal.textPrimary : '#0f172a' }}>
          {popup.title}
        </Typography>
        {popup.content && (
          <Typography
            variant="body2"
            sx={{
              color: isDark ? pal.textSecondary : '#475569',
              display: { xs: 'none', sm: 'inline' },
            }}
          >
            {popup.content}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {popup.ctaText && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onCtaClick(popup)}
            sx={{
              backgroundColor: pal.ctaBg,
              color: pal.ctaText,
              fontWeight: 700,
              textTransform: 'none',
              px: 2,
              py: 0.5,
              fontSize: '0.8rem',
              borderRadius: 1.5,
              '&:hover': { backgroundColor: pal.ctaHover },
            }}
          >
            {popup.ctaText}
          </Button>
        )}
        {popup.canDismiss !== false && (
          <IconButton
            size="small"
            onClick={() => onDismiss(popup)}
            aria-label="Dismiss banner"
            sx={{ color: isDark ? pal.textSecondary : '#64748b' }}
          >
            <X className="w-4 h-4" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

/**
 * Main Popup Manager
 */
const PopupManager = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [popups, setPopups] = useState([]);
  const [pageDismissed, setPageDismissed] = useState(new Set());
  const [modalIndex, setModalIndex] = useState(0);
  const [delayedReadyIds, setDelayedReadyIds] = useState(new Set());

  // Never render storefront popups while inside the admin dashboard
  const isAdminRoute = location.pathname.startsWith('/admin');

  // 1. Fetch active popups once on mount
  useEffect(() => {
    let mounted = true;
    async function fetchPopups() {
      try {
        const list = await getActivePopups();
        if (mounted) {
          setPopups(list);
        }
      } catch (err) {
        console.error('Failed to load active popups:', err);
      }
    }
    fetchPopups();
    return () => {
      mounted = false;
    };
  }, []);

  // 2. Filter popups valid for the current page and current time
  const qualifyingPopups = useMemo(() => {
    if (isAdminRoute || !popups.length) return [];

    const now = new Date();

    return popups
      .filter((p) => {
        // Enabled check
        if (p.enabled === false) return false;

        // Date validity check
        if (p.startDate && new Date(p.startDate) > now) return false;
        if (p.endDate && new Date(p.endDate) < now) return false;

        // Path check
        if (!matchesPath(p.targetPages, location.pathname)) return false;

        // Frequency capping check
        if (isDismissed(p.id, p.dismissFrequency, pageDismissed)) return false;

        return true;
      })
      .sort((a, b) => (a.priority || 5) - (b.priority || 5));
  }, [popups, location.pathname, isAdminRoute, pageDismissed]);

  // Handle delay timers for qualifying popups with delaySeconds > 0
  useEffect(() => {
    const timers = [];

    qualifyingPopups.forEach((p) => {
      const delay = (Number(p.delaySeconds) || 0) * 1000;
      if (delay > 0) {
        const timer = setTimeout(() => {
          setDelayedReadyIds((prev) => new Set(prev).add(p.id));
        }, delay);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [qualifyingPopups]);

  const isPopupReady = useCallback(
    (p) => {
      const delay = Number(p.delaySeconds) || 0;
      if (delay <= 0) return true;
      return delayedReadyIds.has(p.id);
    },
    [delayedReadyIds]
  );

  // Separate into banners and queued modals/cards that are ready
  const activeBanners = useMemo(() => {
    return qualifyingPopups.filter((p) => p.type === 'banner' && isPopupReady(p));
  }, [qualifyingPopups, isPopupReady]);

  const activeModals = useMemo(() => {
    return qualifyingPopups.filter((p) => p.type !== 'banner' && isPopupReady(p));
  }, [qualifyingPopups, isPopupReady]);

  // Keep modalIndex in bounds
  useEffect(() => {
    if (modalIndex >= activeModals.length && activeModals.length > 0) {
      setModalIndex(0);
    }
  }, [activeModals.length, modalIndex]);

  // Dismiss a popup
  const handleDismiss = useCallback((popup) => {
    recordDismissal(popup.id, popup.dismissFrequency);
    setPageDismissed((prev) => new Set(prev).add(popup.id));
  }, []);

  // Dismiss all remaining modals in queue
  const handleDismissAllModals = useCallback(() => {
    activeModals.forEach((p) => {
      recordDismissal(p.id, p.dismissFrequency);
    });
    setPageDismissed((prev) => {
      const next = new Set(prev);
      activeModals.forEach((p) => next.add(p.id));
      return next;
    });
  }, [activeModals]);

  // Advance to next modal in queue
  const handleNextModal = useCallback(() => {
    if (modalIndex + 1 < activeModals.length) {
      setModalIndex((prev) => prev + 1);
    } else {
      setModalIndex(0);
    }
  }, [modalIndex, activeModals.length]);

  // Handle CTA Click
  const handleCtaClick = useCallback(
    (popup) => {
      // Record dismissal on CTA click if configured
      handleDismiss(popup);

      if (!popup.ctaLink) return;

      const link = popup.ctaLink.trim();
      if (link.startsWith('http://') || link.startsWith('https://') || popup.ctaTarget === '_blank') {
        window.open(link, popup.ctaTarget || '_blank');
      } else {
        navigate(link);
      }
    },
    [handleDismiss, navigate]
  );

  if (isAdminRoute) return null;

  const currentModal = activeModals[modalIndex] || null;
  const pal = currentModal ? getThemePalette(currentModal.theme) : null;
  const isDark = currentModal?.theme === 'dark';

  return (
    <>
      {/* 1. TOP ANNOUNCEMENT BANNERS */}
      {activeBanners.length > 0 && (
        <Box sx={{ width: '100%' }}>
          {activeBanners.map((banner) => (
            <BannerItem
              key={banner.id}
              popup={banner}
              onDismiss={handleDismiss}
              onCtaClick={handleCtaClick}
            />
          ))}
        </Box>
      )}

      {/* 2. FLOATING CARDS (Bottom Right Toast) */}
      {currentModal && currentModal.type === 'card' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1300,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: '100%',
            backgroundColor: isDark ? pal.bg : '#ffffff',
            borderRadius: 3,
            border: `1px solid ${pal.border}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            p: 2.5,
            animation: 'fadeInUp 0.3s ease-out',
            '@keyframes fadeInUp': {
              from: { opacity: 0, transform: 'translateY(20px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* Multiple notice navigation badge */}
          {activeModals.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Notice {modalIndex + 1} of {activeModals.length}
              </Typography>
              <Button
                size="small"
                onClick={handleNextModal}
                endIcon={<ChevronRight className="w-3.5 h-3.5" />}
                sx={{ textTransform: 'none', py: 0, fontSize: '0.75rem' }}
              >
                Next
              </Button>
            </Box>
          )}

          {currentModal.canDismiss !== false && (
            <IconButton
              size="small"
              onClick={() => handleDismiss(currentModal)}
              aria-label="Close notice"
              sx={{ position: 'absolute', top: 10, right: 10, color: isDark ? '#94a3b8' : '#64748b' }}
            >
              <X className="w-4 h-4" />
            </IconButton>
          )}

          {currentModal.imageUrl && (
            <Box
              component="img"
              src={currentModal.imageUrl}
              alt=""
              sx={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 2, mb: 1.5 }}
            />
          )}

          {currentModal.badge && (
            <Chip
              label={currentModal.badge}
              size="small"
              sx={{
                backgroundColor: pal.badgeBg,
                color: pal.badgeText,
                fontWeight: 800,
                fontSize: '0.68rem',
                height: 20,
                mb: 1,
              }}
            />
          )}

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: isDark ? pal.textPrimary : '#0f172a', lineHeight: 1.3, mb: 0.5 }}
          >
            {currentModal.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: isDark ? pal.textSecondary : '#475569',
              mb: 2,
              whiteSpace: 'pre-line',
              fontSize: '0.875rem',
            }}
          >
            {currentModal.content}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {currentModal.secondaryCtaText && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDismiss(currentModal)}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
              >
                {currentModal.secondaryCtaText}
              </Button>
            )}
            {currentModal.ctaText && (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleCtaClick(currentModal)}
                sx={{
                  backgroundColor: pal.ctaBg,
                  color: pal.ctaText,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': { backgroundColor: pal.ctaHover },
                }}
              >
                {currentModal.ctaText}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* 3. MODAL DIALOGS (Centered with Backdrop) */}
      {currentModal && currentModal.type === 'modal' && (
        <Dialog
          open={Boolean(currentModal)}
          onClose={currentModal.canDismiss !== false ? () => handleDismiss(currentModal) : undefined}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: isDark ? pal.bg : '#ffffff',
              border: `1px solid ${pal.border}`,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden',
              position: 'relative',
              m: 2,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: currentModal.showBackdrop !== false ? 'rgba(15, 23, 42, 0.65)' : 'transparent',
              backdropFilter: currentModal.showBackdrop !== false ? 'blur(4px)' : 'none',
            },
          }}
        >
          {/* Close button */}
          {currentModal.canDismiss !== false && (
            <IconButton
              size="small"
              onClick={() => handleDismiss(currentModal)}
              aria-label="Close notice"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 10,
                color: isDark ? '#94a3b8' : '#64748b',
                backgroundColor: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.85)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,1)',
                },
              }}
            >
              <X className="w-4 h-4" />
            </IconButton>
          )}

          {/* Header Image if available */}
          {currentModal.imageUrl && (
            <Box
              component="img"
              src={currentModal.imageUrl}
              alt={currentModal.title}
              sx={{ width: '100%', height: { xs: 160, sm: 220 }, objectFit: 'cover', display: 'block' }}
            />
          )}

          <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {/* Multi-modal queue indicator */}
            {activeModals.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(isDark ? '#cbd5e1' : '#64748b', 0.15)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Notice {modalIndex + 1} of {activeModals.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={handleNextModal}
                    endIcon={<ChevronRight className="w-3.5 h-3.5" />}
                    sx={{ textTransform: 'none', py: 0, fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Next Notice
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={handleDismissAllModals}
                    sx={{ textTransform: 'none', py: 0, fontSize: '0.75rem', color: 'text.secondary' }}
                  >
                    Close All
                  </Button>
                </Box>
              </Box>
            )}

            {/* Badge & Label */}
            {currentModal.badge && (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  label={currentModal.badge}
                  size="small"
                  sx={{
                    backgroundColor: pal.badgeBg,
                    color: pal.badgeText,
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    height: 24,
                  }}
                />
              </Box>
            )}

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: isDark ? pal.textPrimary : '#0f172a',
                lineHeight: 1.25,
                mb: 1.5,
              }}
            >
              {currentModal.title}
            </Typography>

            {/* Message Content */}
            <Typography
              variant="body1"
              sx={{
                color: isDark ? pal.textSecondary : '#334155',
                lineHeight: 1.65,
                mb: 3,
                whiteSpace: 'pre-line',
              }}
            >
              {currentModal.content}
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {currentModal.secondaryCtaText && (
                <Button
                  variant="outlined"
                  onClick={() => handleDismiss(currentModal)}
                  sx={{
                    borderColor: isDark ? '#475569' : '#cbd5e1',
                    color: isDark ? '#f1f5f9' : '#475569',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {currentModal.secondaryCtaText}
                </Button>
              )}
              {currentModal.ctaText && (
                <Button
                  variant="contained"
                  onClick={() => handleCtaClick(currentModal)}
                  sx={{
                    backgroundColor: pal.ctaBg,
                    color: pal.ctaText,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    '&:hover': { backgroundColor: pal.ctaHover },
                  }}
                >
                  {currentModal.ctaText}
                </Button>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PopupManager;
